import { EventEmitter } from 'eventemitter3';
import set from 'lodash/set';
import { scheduleAtFixedRate } from './scheduleAtFixedRate';
class Sampler {
    metric;
    listener;
    constructor(metric) {
        this.metric = metric;
        this.listener = (value) => this.onRecord(value);
    }
    start(_now) {
        this.metric.on('record', this.listener);
    }
    stop(_now) {
        this.metric.off('record', this.listener);
    }
}
export class Metric {
    latestValue;
    eventEmitter = new EventEmitter();
    samplerFactory;
    /** @internal */
    constructor(samplerFactory, initialValue) {
        this.samplerFactory = samplerFactory;
        this.latestValue = initialValue;
    }
    record(value) {
        this.latestValue = value;
        this.eventEmitter.emit('record', value);
    }
    getLatestValue() {
        return this.latestValue;
    }
    /** @internal */
    on(eventName, listener) {
        this.eventEmitter.on(eventName, listener);
    }
    /** @internal */
    off(eventName, listener) {
        this.eventEmitter.off(eventName, listener);
    }
    /** @internal */
    createSampler() {
        return this.samplerFactory(this);
    }
}
/*
 * Sum of all records within a sampling period.
 *
 * E.g. count of failed connections
 */
class CountSampler extends Sampler {
    sum = 0;
    onRecord(value) {
        this.sum += value;
    }
    getAggregatedValue() {
        return this.sum;
    }
}
export class CountMetric extends Metric {
    constructor() {
        super((m) => new CountSampler(m));
    }
}
/*
 * Average of all records within a sampling period. If the sampling data is
 * continuous, you may want to use LevelSampler instead.
 *
 * E.g. average latency
 */
class AverageSampler extends Sampler {
    sum = 0;
    count = 0;
    onRecord(value) {
        this.sum += value;
        this.count++;
    }
    getAggregatedValue() {
        if (this.count > 0) {
            return this.sum / this.count;
        }
        else {
            return undefined;
        }
    }
}
export class AverageMetric extends Metric {
    constructor() {
        super((m) => new AverageSampler(m));
    }
}
/*
 * Average level of the records during a sampling period. Takes the average of the
 * recorded values, but also includes the current level as a first sample when
 * the sampling starts.
 *
 * E.g. average count of currently active connections
 */
class LevelSampler extends AverageSampler {
    start(now) {
        super.start(now);
        const latest = this.metric.getLatestValue();
        if (latest !== undefined) {
            this.onRecord(latest);
        }
    }
}
export class LevelMetric extends Metric {
    constructor(initialValue) {
        super((m) => new LevelSampler(m), initialValue);
    }
}
/*
 * Sum of records divided by seconds.
 *
 * E.g. download speed (bytes per second)
 */
class RateSampler extends Sampler {
    sum = 0;
    startTimestamp = undefined;
    stopTimestamp = undefined;
    start(now) {
        super.start(now);
        this.startTimestamp = now;
    }
    stop(now) {
        super.stop(now);
        this.stopTimestamp = now;
    }
    onRecord(value) {
        this.sum += value;
    }
    getAggregatedValue() {
        if ((this.startTimestamp !== undefined) && (this.stopTimestamp !== undefined) && (this.startTimestamp !== this.stopTimestamp)) {
            const elapsedSeconds = (this.stopTimestamp - this.startTimestamp) / 1000;
            return this.sum / elapsedSeconds;
        }
        else {
            return undefined;
        }
    }
}
export class RateMetric extends Metric {
    constructor() {
        super((m) => new RateSampler(m));
    }
}
export class MetricsContext {
    metrics = new Map();
    addMetrics(namespace, definitions) {
        Object.keys(definitions).forEach((key) => {
            const id = `${namespace}.${key}`;
            if (this.metrics.has(id)) {
                throw new Error(`Metrics "${id}" already created`);
            }
            this.metrics.set(id, definitions[key]);
        });
    }
    createReportProducer(onReport, interval, abortSignal, formatNumber) {
        const ongoingSamples = new Map();
        scheduleAtFixedRate(async (now) => {
            if (ongoingSamples.size > 0) {
                const report = {
                    period: {
                        start: now - interval,
                        end: now
                    }
                };
                ongoingSamples.forEach((sample, metricId) => {
                    sample.stop(now);
                    const value = sample.getAggregatedValue();
                    if (value !== undefined) {
                        set(report, metricId, (formatNumber !== undefined) ? formatNumber(value) : value);
                    }
                });
                onReport(report);
                ongoingSamples.clear();
            }
            this.metrics.forEach((metric, id) => {
                const sample = metric.createSampler();
                sample.start(now);
                ongoingSamples.set(id, sample);
            });
        }, interval, abortSignal);
    }
    getMetric(id) {
        return this.metrics.get(id);
    }
}
